import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import logo from '@/assets/logo-insane-force.jpg';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * Welcome / Landing — Insane Force Team
 * "Cinematic Marquee" editorial
 *
 * Design notes:
 *  - Full-bleed cinematic layout: marquee tipográfico + dragon mark central
 *  - Soporta light/dark vía ThemeContext del proyecto
 *  - Acento rojo desde tokens CSS (--accent) — respeta el design system
 *  - Responsive via ResizeObserver (≤640px = layout móvil)
 *  - CTAs grandes (min 44px) con estética HUD
 */

const ACCENT_RED_DARK = '#f04545';   // hsl(0, 80%, 58%)
const ACCENT_RED_LIGHT = '#dc2626';  // hsl(0, 75%, 50%)

export default function WelcomePage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const bg = isDark ? '#060606' : '#ededE8';
  const fg = isDark ? '#f5f5f0' : '#0a0a0a';
  const sub = isDark ? 'rgba(245,245,240,0.4)' : 'rgba(10,10,10,0.4)';
  const line = isDark ? 'rgba(245,245,240,0.14)' : 'rgba(10,10,10,0.14)';
  const accent = isDark ? ACCENT_RED_DARK : ACCENT_RED_LIGHT;

  const containerRef = useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setNarrow(e.contentRect.width < 640);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="min-h-screen"
      style={{
        background: bg,
        color: fg,
        fontFamily: "'Oswald', sans-serif",
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
      }}
    >
      {/* Theme toggle */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle variant="navbar" />
      </div>

      {/* Background rings */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `
            radial-gradient(ellipse 60% 55% at 50% 50%, ${accent}22 0%, transparent 55%),
            radial-gradient(ellipse 90% 70% at 50% 50%, ${isDark ? '#1a0606' : '#fff'} 0%, transparent 60%)
          `,
        }}
      />

      {/* Grid overlay */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.35,
          backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`,
          backgroundSize: narrow ? '40px 40px' : '80px 80px',
          maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
        }}
      />

      {/* Top HUD */}
      <header
        style={{
          position: 'relative',
          zIndex: 4,
          padding: narrow ? '14px 18px' : '18px 32px',
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr auto' : '1fr auto 1fr',
          alignItems: 'center',
          gap: narrow ? 10 : 20,
          fontFamily: "'Space Grotesk', sans-serif",
          borderBottom: `1px solid ${line}`,
          background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}
      >
        {/* LEFT — live tag */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            fontSize: narrow ? 9 : 10,
            letterSpacing: narrow ? '0.2em' : '0.3em',
            textTransform: 'uppercase',
            color: accent,
            fontWeight: 600,
            minWidth: 0,
          }}
        >
          <span
            style={{
              position: 'relative',
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: accent,
              boxShadow: `0 0 10px ${accent}, 0 0 20px ${accent}66`,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: 'absolute',
                inset: -4,
                borderRadius: '50%',
                border: `1px solid ${accent}`,
                animation: 'ift-ping 2s ease-out infinite',
              }}
            />
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            AI Training App
          </span>
        </div>

        {/* CENTER — Powered by (desktop) */}
        {!narrow && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ width: 24, height: 1, background: `linear-gradient(90deg, transparent, ${fg}55)` }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span style={{ fontSize: 8, letterSpacing: '0.45em', textTransform: 'uppercase', color: sub }}>
                Powered by
              </span>
              <span
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontStyle: 'italic',
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: fg,
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: 6,
                }}
              >
                Insane <span style={{ color: accent }}>Force</span> Team
                <sup
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontStyle: 'normal',
                    fontSize: 8,
                    fontWeight: 600,
                    letterSpacing: 0,
                    color: accent,
                    marginLeft: 2,
                    top: '-0.6em',
                    position: 'relative',
                  }}
                >
                  ®
                </sup>
                <span
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontStyle: 'normal',
                    fontSize: 9,
                    letterSpacing: '0.2em',
                    color: sub,
                    marginLeft: 4,
                  }}
                >
                  © 2026
                </span>
              </span>
            </div>
            <span style={{ width: 24, height: 1, background: `linear-gradient(90deg, ${fg}55, transparent)` }} />
          </div>
        )}

        {/* RIGHT — spacer (theme toggle is fixed) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontSize: narrow ? 9 : 10,
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: sub,
          }}
        >
          <span style={{ opacity: 0 }}>·</span>
        </div>

        {/* MOBILE — one-line Powered-by */}
        {narrow && (
          <div
            style={{
              gridColumn: '1 / -1',
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: 5,
              marginTop: 8,
              paddingTop: 8,
              borderTop: `1px solid ${line}`,
              fontFamily: "'Oswald', sans-serif",
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '0.3em',
                color: fg,
                opacity: 0.9,
                textTransform: 'uppercase',
              }}
            >
              Powered by
            </span>
            <span
              style={{
                fontStyle: 'italic',
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: '0.1em',
                color: fg,
              }}
            >
              Insane <span style={{ color: accent }}>Force</span> Team
            </span>
            <sup
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontStyle: 'normal',
                fontSize: 8,
                fontWeight: 700,
                color: accent,
                top: '-0.55em',
                position: 'relative',
              }}
            >
              ®
            </sup>
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontStyle: 'normal',
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: '0.22em',
                color: fg,
                opacity: 0.75,
              }}
            >
              © 2026
            </span>
          </div>
        )}
      </header>

      {/* MAIN stage */}
      <main
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `${narrow ? 10 : 20}px 0`,
          overflow: 'hidden',
        }}
      >
        {/* Marquee band */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: narrow ? 14 : 24,
            zIndex: 1,
            maskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
            opacity: 0.85,
          }}
        >
          <MarqueeRow speed="52s" fg={fg} narrow={narrow}
            text="INSANE FORCE TEAM · SMART PERFORMANCE · COACH & CLIENT PORTAL · " />
          <MarqueeRow speed="68s" reverse fg={fg} outline narrow={narrow}
            text="DISCIPLINE · DATA · COMMUNITY · DISCIPLINE · DATA · COMMUNITY · " />
          <MarqueeRow speed="58s" fg={fg} narrow={narrow}
            text="ENTRENA CON DATOS · ENTRENA COMO UN EQUIPO · " />
        </div>

        {/* Center dragon + name */}
        <div style={{ position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Safe-zone halo */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: narrow ? 360 : 560,
              height: narrow ? 320 : 440,
              background: `radial-gradient(ellipse at center, ${bg} 0%, ${bg}ee 40%, transparent 75%)`,
              pointerEvents: 'none',
              zIndex: -1,
            }}
          />

          {/* Dragon disc */}
          <div
            style={{
              position: 'relative',
              width: narrow ? 180 : 240,
              height: narrow ? 180 : 240,
              borderRadius: '50%',
              background: bg,
              boxShadow: `
                0 0 0 1px ${accent}55,
                0 0 0 10px ${bg},
                0 0 0 11px ${accent}33,
                0 0 70px ${accent}55
              `,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={logo}
              alt="Insane Force Team"
              style={{
                width: '96%',
                height: '96%',
                objectFit: 'cover',
                mixBlendMode: isDark ? 'screen' : 'multiply',
                filter: 'contrast(1.2)',
              }}
            />
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(180deg, transparent 0%, transparent 48%, ${accent}66 50%, transparent 52%, transparent 100%)`,
                animation: 'ift-scan 4s linear infinite',
                mixBlendMode: 'screen',
              }}
            />
          </div>

          {/* Name block */}
          <div style={{ marginTop: narrow ? 18 : 26, textAlign: 'center', position: 'relative' }}>
            <div
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: narrow ? 9 : 10,
                letterSpacing: '0.5em',
                color: accent,
                marginBottom: 10,
                textTransform: 'uppercase',
              }}
            >
              ◆ Team Portal ◆
            </div>
            <div
              style={{
                fontSize: narrow ? 26 : 36,
                fontWeight: 700,
                fontStyle: 'italic',
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              Insane <span style={{ color: accent }}>Force</span> Team
            </div>
          </div>
        </div>
      </main>

      {/* Bottom CTAs — dos paths claros:
            izquierda: ENTRA (cliente o coach ya registrado)
            derecha:   CREA TU CUENTA (coach nuevo que quiere usar la
                       herramienta para gestionar sus clientes)

          El registro de clientes finales es exclusivamente por link
          de invitación de un coach — no aparece en el welcome porque
          es funnel coach→cliente, no inbound. */}
      <footer
        style={{
          position: 'relative',
          zIndex: 2,
          borderTop: `1px solid ${line}`,
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
          gap: narrow ? 10 : 16,
          padding: narrow ? '16px 20px' : '24px 36px',
        }}
      >
        <HUDCta
          label="¿ YA ERES PARTE DEL EQUIPO ?"
          title="ENTRA"
          accent={accent}
          fg={fg}
          sub={sub}
          line={line}
          primary={false}
          narrow={narrow}
          isDark={isDark}
          onClick={() => navigate('/auth?tab=login')}
        />
        <HUDCta
          label="¿ ERES COACH NUEVO ?"
          title="CREA TU CUENTA"
          accent={accent}
          fg={fg}
          sub={sub}
          line={line}
          primary={true}
          narrow={narrow}
          isDark={isDark}
          onClick={() => navigate('/coach/signup')}
        />
      </footer>

      {/* Keyframes */}
      <style>{`
        @keyframes ift-scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes ift-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes ift-marquee-rev {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        @keyframes ift-ping {
          0% { transform: scale(1); opacity: 0.9; }
          100% { transform: scale(2.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/* ---------- Subcomponents ---------- */

interface MarqueeRowProps {
  text: string;
  speed: string;
  reverse?: boolean;
  fg: string;
  outline?: boolean;
  narrow: boolean;
}

function MarqueeRow({ text, speed, reverse, fg, outline, narrow }: MarqueeRowProps) {
  const style: React.CSSProperties = outline
    ? { WebkitTextStroke: `1.2px ${fg}`, color: 'transparent' }
    : { color: fg };
  return (
    <div
      style={{
        display: 'flex',
        whiteSpace: 'nowrap',
        fontSize: narrow ? 'clamp(36px, 14vw, 64px)' : 'clamp(54px, 7.5vw, 110px)',
        fontWeight: 700,
        fontStyle: 'italic',
        letterSpacing: '-0.02em',
        textTransform: 'uppercase',
        animation: `${reverse ? 'ift-marquee-rev' : 'ift-marquee'} ${speed} linear infinite`,
        opacity: outline ? 0.9 : 0.55,
        ...style,
      }}
    >
      <span style={{ paddingRight: 40 }}>{text.repeat(3)}</span>
    </div>
  );
}

interface HUDCtaProps {
  label: string;
  title: string;
  accent: string;
  fg: string;
  sub: string;
  line: string;
  primary: boolean;
  narrow: boolean;
  isDark: boolean;
  onClick: () => void;
}

function HUDCta({ label, title, accent, fg, sub, line, primary, narrow, isDark, onClick }: HUDCtaProps) {
  const [hover, setHover] = useState(false);
  const bg = primary ? accent : isDark ? 'rgba(0,0,0,0.4)' : '#0a0a0a';
  const color = primary ? '#fff' : isDark ? fg : '#f5f5f0';
  const borderColor = primary ? accent : isDark ? line : '#0a0a0a';

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: narrow ? '14px 18px' : '18px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        textDecoration: 'none',
        color,
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 0,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        cursor: 'pointer',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover ? `0 8px 30px ${accent}55` : 'none',
        transition: 'all 0.25s ease',
        minHeight: 44,
        gap: 12,
        width: '100%',
        textAlign: 'left',
        fontFamily: "'Oswald', sans-serif",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: narrow ? 12 : 14,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: primary ? 'rgba(255,255,255,0.95)' : 'rgba(245,245,240,0.9)',
            marginBottom: 6,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: narrow ? 22 : 26,
            fontWeight: 700,
            fontStyle: 'italic',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          {title}
        </div>
      </div>
      <span
        style={{
          fontSize: 22,
          transform: hover ? 'translateX(6px)' : 'translateX(0)',
          transition: 'transform 0.25s ease',
          flexShrink: 0,
        }}
      >
        →
      </span>
    </button>
  );
}
